import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { Button, Container, Dialog, DialogContent, Paper } from '@mui/material'
import moment from 'moment'
import { capitalize } from 'lodash'
import { DataGridPro, GridColumns } from '@mui/x-data-grid-pro'
import { PatientTransportStatus } from '@services/api'
import { findMedicalOrders } from '@state/thunks/medicalOrderThunk'
import './MedicalOrderList.css'
import { TransportForm } from './TransportForm'
import { setMedicalOrderFilters } from '@state/reducers/medicalOrderReducer'
import { DialogTitle } from '@components/titles'

const AssignMedicalOrder = () => {
  const [selectedId, setSelectedId] = useState<number>(-1)

  const medicalOrders = useAppSelector(
    (state) => state.medicalOrder.medicalOrders,
  )
  const filters = useAppSelector((state) => state.medicalOrder.filters)
  const dispatch = useAppDispatch()
  const columns: GridColumns = [
    {
      field: 'accessionNumber',
      headerName: 'N° d\'examen',
      width: 160,
    },
    {
      field: 'patient.firstName',
      headerName: 'Prénom',
      width: 160,
      valueGetter: (params) => capitalize(params.row.patient.firstName),
    },
    {
      field: 'patient.lastName',
      headerName: 'Nom',
      width: 160,
      valueGetter: ({ row }) => {
        return capitalize(row.patient.lastName)
      },
    },
    {
      field: 'patient.birthDate',
      headerName: 'Date de naissance',
      width: 160,
      valueGetter: ({ row }) => {
        return moment(row.patient.birthDate).format('DD/MM/YYYY')
      },
    },
    {
      field: 'exam.label',
      headerName: 'Examen',
      width: 260,
      valueGetter: ({ row }) => {
        return `${capitalize(row.exam.label)} (${row.exam.code})`
      },
    },

    {
      field: 'plannedDate',
      headerName: 'Heure RDV',
      width: 100,
      renderCell: (params) => {
        return (
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {moment(params.row.plannedDate).format('HH:mm')}
          </div>
        )
      },
    },
    {
      field: 'patientTransport',
      headerName: 'Statut Transport',
      width: 280,
      renderCell: (params) => {
        return (
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {params.row.patientTransport
              ? params.row.patientTransport.status ===
                PatientTransportStatus.Called
                ? `Appelé à ${moment(
                    params.row.patientTransport.calledAt,
                  ).format('HH:mm')} par ${params.row.patientTransport.caller}`
                : 'Non appelé'
              : 'Non appelé'}{' '}
            {}
          </div>
        )
      },
    },
    {
      field: 'actions',
      headerName: 'Transports',
      width: 200,
      renderCell: (params) => {
        return (
          <>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSelectedId(params.row.id)
              }}
              sx={{
                backgroundColor: '#f5f5f5',
                color: 'black',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
              size="small"
            >
              {params.row.patientTransport
                ? params.row.patientTransport.status ===
                  PatientTransportStatus.Called
                  ? 'Voir'
                  : 'Modifier / Appeller'
                : 'Ajouter'}
            </Button>
          </>
        )
      },
    },
  ]
  useEffect(() => {
    dispatch(findMedicalOrders({ includeTransport: true }))
  }, [filters.page])

  if (!medicalOrders) {
    return <></>
  }
  const selectedMedicalOrder = useMemo(
    () => medicalOrders.find((mo) => mo.id === selectedId),
    [selectedId, medicalOrders],
  )
  return (
    <Container maxWidth={false} style={{ height: '89vh', padding: '0px 5px' }}>
      <Paper
        elevation={2}
        style={{
          width: '100%',
          borderRadius: '0 15px 15px 15px',
          overflow: 'hidden',
        }}
      >
        <DataGridPro
          columns={columns}
          rows={medicalOrders}
          getRowClassName={(params) =>
            params.row.patientTransport
              ? params.row.patientTransport.status ===
                PatientTransportStatus.Called
                ? 'called-row'
                : 'uncalled-row'
              : ''
          }
          rowCount={filters.total}
          disableSelectionOnClick
          pagination
          paginationMode="server"
          disableMultipleSelection
          onPageChange={(page) => {
            dispatch(setMedicalOrderFilters({ ...filters, page }))
          }}
          initialState={{
            pinnedColumns: { left: [''], right: ['actions'] },
          }}
        />
      </Paper>
      {/* add dialog title with close button */}
      <Dialog
        open={selectedId !== -1}
        onClose={() => setSelectedId(-1)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 5,
            backgroundColor: '#f5f5f5',
          },
        }}
      >
        <DialogTitle
          title="Transport"
          onClose={() => setSelectedId(-1)}
          format
        />
        <DialogContent>
          {!!selectedMedicalOrder && (
            <TransportForm row={selectedMedicalOrder} />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  )
}

export default AssignMedicalOrder
